/**
 * AffiliateSignupPage — Publieke pagina voor affiliate-aanmelding (P3).
 *
 * Geen auth vereist. Form post naar /api/public/affiliates/apply.
 * 20% commissie van eerste maand per conversie.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Gift, TrendingUp, CheckCircle, Loader2, ArrowRight, ArrowLeft } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function AffiliateSignupPage() {
  const [form, setForm] = useState({
    full_name: '', email: '', company_name: '', website_or_channel: '', audience_size: '', why_join: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error('Vul naam en e-mail in');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${BACKEND}/api/public/affiliates/apply`, form);
      setSubmitted(true);
      toast.success('Aanmelding ontvangen — wij nemen binnen 5 werkdagen contact op');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verzenden mislukt');
    }
    setSaving(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: 'white', padding: '40px 16px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <Link to="/" style={{ color: '#94a3b8', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', marginBottom: 24 }}>
          <ArrowLeft size={14} /> Terug naar website
        </Link>

        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <p style={{ color: '#D4A017', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Affiliate-programma</p>
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: 36, fontWeight: 800, marginTop: 8, lineHeight: 1.1 }}>
            Verdien 20% per nieuwe klant
          </h1>
          <p style={{ color: '#94a3b8', fontSize: 15, marginTop: 12, maxWidth: 540, marginInline: 'auto' }}>
            Deel uw unieke promo-link en ontvang 20% van de eerste maand zodra een klant zich aanmeldt. Geen lock-in, geen fees.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 32 }}>
          {[
            { icon: Gift, t: '20%', s: 'Van eerste maand' },
            { icon: TrendingUp, t: '€999+', s: 'Per Managed AI sale' },
            { icon: Sparkles, t: 'Direct', s: 'Promo URL klaar' },
          ].map((b, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: 16, border: '1px solid rgba(212,160,23,0.2)', textAlign: 'center' }}>
              <b.icon size={20} style={{ color: '#D4A017', margin: '0 auto 8px' }} />
              <p style={{ fontFamily: "'Sora', sans-serif", fontSize: 22, fontWeight: 800 }}>{b.t}</p>
              <p style={{ fontSize: 11, color: '#94a3b8' }}>{b.s}</p>
            </div>
          ))}
        </div>

        {submitted ? (
          <div data-testid="affiliate-signup-success" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 16, padding: 32, textAlign: 'center' }}>
            <CheckCircle size={48} color="#22c55e" style={{ margin: '0 auto 12px' }} />
            <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: 22, fontWeight: 800 }}>Aanmelding ontvangen</h2>
            <p style={{ color: '#bbf7d0', fontSize: 14, marginTop: 6 }}>Wij beoordelen binnen 5 werkdagen en e-mailen u zodra uw promo-link klaar is.</p>
          </div>
        ) : (
          <form onSubmit={submit} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 16, padding: 28, border: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
              <Lbl label="Naam *">
                <input data-testid="aff-fullname" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} style={inputDark} />
              </Lbl>
              <Lbl label="E-mailadres *">
                <input data-testid="aff-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={inputDark} />
              </Lbl>
              <Lbl label="Bedrijfsnaam (optioneel)">
                <input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} style={inputDark} />
              </Lbl>
              <Lbl label="Audience grootte (volgers, mailing list etc)">
                <input value={form.audience_size} onChange={(e) => setForm({ ...form, audience_size: e.target.value })} placeholder="bv. 5.000 LinkedIn volgers" style={inputDark} />
              </Lbl>
            </div>
            <div style={{ marginTop: 14 }}>
              <Lbl label="Website / kanaal">
                <input data-testid="aff-channel" value={form.website_or_channel} onChange={(e) => setForm({ ...form, website_or_channel: e.target.value })} placeholder="https://uwblog.nl of @handle" style={inputDark} />
              </Lbl>
            </div>
            <div style={{ marginTop: 14 }}>
              <Lbl label="Waarom wilt u IMPERIA AI aanbevelen?">
                <textarea value={form.why_join} onChange={(e) => setForm({ ...form, why_join: e.target.value })} rows={4} style={{ ...inputDark, resize: 'vertical' }} />
              </Lbl>
            </div>
            <button type="submit" disabled={saving} data-testid="aff-submit" style={{ marginTop: 18, width: '100%', background: '#D4A017', color: '#0f172a', fontWeight: 800, padding: '14px 16px', borderRadius: 10, border: 'none', cursor: saving ? 'wait' : 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {saving ? 'Verzenden…' : 'Aanmelden voor affiliate-programma'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const inputDark = { width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 14, color: 'white', boxSizing: 'border-box' };

function Lbl({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4, display: 'block' }}>{label}</label>
      {children}
    </div>
  );
}
